import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, CheckCircle2, Circle, Compass, Flame, Scale, Sparkles, BookOpen, RefreshCw, Repeat, ListChecks, Check, BarChart3, Bell, Trophy, X } from "lucide-react";
import { useLifeState } from "@/context/LifeStateContext";
import {
  areas, priorities, iso, uid, pointsFor, timeLabel, computeLifeMetrics, verseFor,
  recurrenceOpts, recurrenceLabel, taskDoneOn, isRecurring,
  habitDoneOn, habitStreak, habitTotal, lastNDates,
  habitBestStreak, monthMatrix, habitMonthStats, monthLabel,
} from "@/lib/life";
import { dayLabel } from "@/lib/wellness";
import { lifeApi } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard, EmptyState, AddButton, ProgressBar, StatusPill } from "@/components/shared";
import { FormModal, TextField, SelectField, TextArea } from "@/components/forms";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const areaOpts = areas.map((a) => ({ value: a.name, label: a.name }));
const prioOpts = priorities.map((p) => ({ value: p, label: p }));
const areaColor = (name) => (areas.find((a) => a.name === name)?.color || "#1f7355");
const prioTone = (p) => (/(critical|must)/i.test(p) ? "high" : /high/i.test(p) ? "med" : "low");
const weekdayShort = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "narrow" });
const dayNum = (d) => new Date(d + "T12:00:00").getDate();
const WEEKDAY_HEAD = ["S", "M", "T", "W", "T", "F", "S"];

// ---------------- Habit insights (monthly heatmap + streak stats) ----------------
function HabitInsights({ habit, log, color, open, onOpenChange }) {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const todayIso = iso();
  const weeks = monthMatrix(ym.y, ym.m);
  const stats = habitMonthStats(log, habit.id, ym.y, ym.m);
  const best = habitBestStreak(log, habit.id);
  const current = habitStreak(log, habit.id, todayIso);
  const total = habitTotal(log, habit.id);
  const stepMonth = (delta) => {
    const d = new Date(ym.y, ym.m + delta, 1);
    setYm({ y: d.getFullYear(), m: d.getMonth() });
  };
  const isFutureMonth = ym.y > now.getFullYear() || (ym.y === now.getFullYear() && ym.m >= now.getMonth());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--surface-2)] border border-[color:var(--border-subtle)] max-w-md" data-testid="habit-insights-modal">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl text-[var(--text)] flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />{habit.name}
          </DialogTitle>
          <DialogDescription className="text-[var(--text-muted)]">Momentum at a glance — every filled day is a choice that compounds.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-[10px] border border-[color:var(--border-subtle)] bg-white p-3 text-center" data-testid="insights-current-streak">
            <div className="flex items-center justify-center gap-1 text-[var(--gold)]"><Flame className="h-4 w-4" /><span className="font-serif-display text-2xl text-[var(--text)] tabular-nums">{current}</span></div>
            <div className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] mt-1">Current</div>
          </div>
          <div className="rounded-[10px] border border-[color:var(--border-subtle)] bg-white p-3 text-center" data-testid="insights-best-streak">
            <div className="flex items-center justify-center gap-1 text-[var(--green)]"><Trophy className="h-4 w-4" /><span className="font-serif-display text-2xl text-[var(--text)] tabular-nums">{best}</span></div>
            <div className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] mt-1">Best</div>
          </div>
          <div className="rounded-[10px] border border-[color:var(--border-subtle)] bg-white p-3 text-center" data-testid="insights-total">
            <div className="flex items-center justify-center gap-1 text-[var(--text)]"><CheckCircle2 className="h-4 w-4 text-[var(--green)]" /><span className="font-serif-display text-2xl text-[var(--text)] tabular-nums">{total}</span></div>
            <div className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] mt-1">Total days</div>
          </div>
        </div>

        <div className="mt-2 rounded-[10px] border border-[color:var(--border-subtle)] bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => stepMonth(-1)} data-testid="insights-month-prev" aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="text-sm font-medium text-[var(--text)]" data-testid="insights-month-label">{monthLabel(ym.y, ym.m)}</div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => stepMonth(1)} disabled={isFutureMonth} data-testid="insights-month-next" aria-label="Next month"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-7 gap-1.5 mb-1.5">
            {WEEKDAY_HEAD.map((w, i) => (<div key={i} className="text-center text-[10px] uppercase tracking-wide text-[var(--text-faint)]">{w}</div>))}
          </div>
          <div className="space-y-1.5">
            {weeks.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-1.5">
                {week.map((d, di) => {
                  if (!d) return <div key={di} />;
                  const on = habitDoneOn(log, habit.id, d);
                  const isToday = d === todayIso;
                  const future = d > todayIso;
                  return (
                    <div key={di}
                      data-testid={`heatmap-cell-${d}`}
                      title={dayLabel(d)}
                      className={cn(
                        "aspect-square rounded-[6px] grid place-items-center text-[11px] tabular-nums border transition-colors",
                        on ? "text-white border-transparent" : "border-[color:var(--border-subtle)]",
                        !on && future && "opacity-40",
                        isToday && "ring-2 ring-offset-1 ring-[color:rgba(198,139,60,0.5)]"
                      )}
                      style={on ? { backgroundColor: color } : { color: "var(--text-faint)" }}
                    >
                      {dayNum(d)}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[color:var(--border-subtle)]">
            <span className="text-xs text-[var(--text-muted)]">This month</span>
            <span className="text-sm text-[var(--text)]" data-testid="insights-month-rate">{stats.done}/{stats.total} · {stats.rate}%</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


const Ring = ({ value }) => {
  const r = 52, c = 2 * Math.PI * r, off = c - (value / 100) * c;
  return (
    <div className="relative h-[136px] w-[136px]">
      <svg viewBox="0 0 132 132" className="h-full w-full -rotate-90">
        <circle cx="66" cy="66" r={r} fill="none" stroke="rgba(23,35,31,0.08)" strokeWidth="12" />
        <circle cx="66" cy="66" r={r} fill="none" stroke="var(--green)" strokeWidth="12" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} className="transition-all duration-500" />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center"><div className="font-serif-display text-3xl text-[var(--text)] tabular-nums" data-testid="readiness-value">{value}</div><div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">points</div></div>
      </div>
    </div>
  );
};

export default function Command() {
  const { state, update, loading, wealthStewardship, wellnessVitality } = useLifeState();
  const [date, setDate] = useState(iso());
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [quick, setQuick] = useState("");
  const [visionOpen, setVisionOpen] = useState(false);
  const [vision, setVision] = useState(null);
  const [habitOpen, setHabitOpen] = useState(false);
  const [habitDraft, setHabitDraft] = useState(null);
  const [insightOpen, setInsightOpen] = useState(false);
  const [insightHabit, setInsightHabit] = useState(null);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [encLoading, setEncLoading] = useState(false);
  const encRequested = useRef(null);

  const overridesFor = () => {
    const o = {};
    if (wealthStewardship) o["Wealth & Stewardship"] = wealthStewardship.score;
    if (wellnessVitality) o["Health & Vitality"] = wellnessVitality.score;
    return o;
  };

  const generateEnc = useCallback(async () => {
    if (!state) return;
    setEncLoading(true);
    try {
      const mm = computeLifeMetrics(state, date, overridesFor());
      const res = await lifeApi.encouragement({
        date, lifeScore: mm.lifeScore, readiness: mm.earned, planned: mm.planned,
        energy: state.energy, alignment: state.alignment,
        lowestArea: mm.lowest?.area?.name, nextAction: mm.next?.title,
      });
      update((prev) => ({ ...prev, encouragements: { ...(prev.encouragements || {}), [date]: { ...res, generatedAt: new Date().toISOString() } } }), "Generated encouragement");
    } catch (e) {
      toast.error("Couldn't generate encouragement right now");
      encRequested.current = null;
    } finally {
      setEncLoading(false);
    }
  }, [state, date, wealthStewardship, wellnessVitality, update]);

  // Auto-generate once per day (cached), guarded against duplicate calls.
  useEffect(() => {
    if (!state) return;
    if (state.encouragements?.[date]) return;
    if (encRequested.current === date) return;
    encRequested.current = date;
    generateEnc();
  }, [state, date, generateEnc]);

  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const overrides = overridesFor();
  const m = computeLifeMetrics(state, date, overrides);
  const enc = state.encouragements?.[date];
  const verse = verseFor(date);
  const isToday = date === iso();
  const shift = (days) => { const d = new Date(date + "T12:00:00"); d.setDate(d.getDate() + days); setDate(iso(d)); };

  // ---------- Actions ----------
  const dayTasks = [...m.dayTasks].sort((a, b) => (a.time || "99").localeCompare(b.time || "99"));
  const doneCount = dayTasks.filter((t) => taskDoneOn(t, date)).length;

  const openNew = () => { setDraft({ id: uid("a"), title: "", date, time: "09:00", endTime: "", engine: areas[0].name, priority: "Medium", points: 10, recurrence: "once" }); setOpen(true); };
  const openEdit = (t) => { setDraft({ ...t, endTime: t.endTime || "", recurrence: t.recurrence || "once" }); setOpen(true); };
  const saveTask = () => {
    if (!draft.title.trim()) return toast.error("Title is required");
    const rec = draft.recurrence || "once";
    const item = {
      ...draft, title: draft.title.trim(), points: Number(draft.points) || pointsFor(draft.priority),
      endTime: draft.endTime || undefined, recurrence: rec,
    };
    if (rec !== "once" && !Array.isArray(item.doneDates)) item.doneDates = [];
    update((prev) => {
      const exists = (prev.tasks || []).some((x) => x.id === item.id);
      return { ...prev, tasks: exists ? prev.tasks.map((x) => (x.id === item.id ? { ...x, ...item } : x)) : [...(prev.tasks || []), { ...item, done: rec === "once" ? false : undefined }] };
    }, "Updated today's plan");
    setOpen(false); toast.success("Action saved");
  };
  const quickAdd = () => {
    const title = quick.trim();
    if (!title) return;
    const item = { id: uid("a"), title, date, time: "09:00", endTime: undefined, engine: areas[0].name, priority: "Medium", points: 10, recurrence: "once", done: false };
    update((prev) => ({ ...prev, tasks: [...(prev.tasks || []), item] }), "Added action");
    setQuick("");
  };
  const toggle = (t) => {
    const wasDone = taskDoneOn(t, date);
    update((prev) => ({
      ...prev,
      tasks: prev.tasks.map((x) => {
        if (x.id !== t.id) return x;
        if ((x.recurrence || "once") === "once") return { ...x, done: !x.done };
        const set = new Set(x.doneDates || []);
        if (set.has(date)) set.delete(date); else set.add(date);
        return { ...x, doneDates: [...set] };
      }),
    }), "Updated action");
    if (!wasDone) toast.success(`+${t.points || pointsFor(t.priority)} points`, { duration: 1400 });
  };
  const remove = (id) => { update((prev) => ({ ...prev, tasks: prev.tasks.filter((t) => t.id !== id) }), "Removed action"); toast.success("Action removed"); };
  const setField = (k, v) => update((prev) => ({ ...prev, [k]: v }), "Updated readiness");

  const openVision = () => { setVision({ identity: state.identity, calling: state.calling, season: state.season, focus: state.focus }); setVisionOpen(true); };
  const saveVision = () => { update((prev) => ({ ...prev, ...vision }), "Updated vision"); setVisionOpen(false); toast.success("Vision updated"); };

  // ---------- Habits ----------
  const habits = state.habits || [];
  const habitLog = state.habitLog || {};
  const week = lastNDates(date, 7);
  const openInsights = (h) => { setInsightHabit(h); setInsightOpen(true); };
  const openNewHabit = () => { setHabitDraft({ id: uid("h"), name: "", area: areas[0].name }); setHabitOpen(true); };
  const openEditHabit = (h) => { setHabitDraft({ ...h }); setHabitOpen(true); };
  const saveHabit = () => {
    if (!habitDraft.name.trim()) return toast.error("Name is required");
    const item = { ...habitDraft, name: habitDraft.name.trim() };
    update((prev) => {
      const exists = (prev.habits || []).some((x) => x.id === item.id);
      return { ...prev, habits: exists ? prev.habits.map((x) => (x.id === item.id ? { ...x, ...item } : x)) : [...(prev.habits || []), item] };
    }, "Updated habits");
    setHabitOpen(false); toast.success("Habit saved");
  };
  const removeHabit = (id) => {
    update((prev) => {
      const log = { ...(prev.habitLog || {}) }; delete log[id];
      return { ...prev, habits: (prev.habits || []).filter((h) => h.id !== id), habitLog: log };
    }, "Removed habit");
    toast.success("Habit removed");
  };
  const toggleHabit = (id, d) => update((prev) => {
    const log = { ...(prev.habitLog || {}) };
    const days = { ...(log[id] || {}) };
    if (days[d]) delete days[d]; else days[d] = true;
    log[id] = days;
    return { ...prev, habitLog: log };
  }, "Updated habit");

  // ---------- Smart reminders (gentle nudge for today) ----------
  const pendingTasks = dayTasks.filter((t) => !taskDoneOn(t, date));
  const pendingHabits = habits.filter((h) => !habitDoneOn(habitLog, h.id, date));
  const nudgeItems = [
    ...pendingTasks.map((t) => ({ kind: "task", id: t.id, label: t.title, color: areaColor(t.engine), onDo: () => toggle(t) })),
    ...pendingHabits.map((h) => ({ kind: "habit", id: h.id, label: h.name, color: areaColor(h.area), onDo: () => toggleHabit(h.id, date) })),
  ];
  const showNudge = isToday && !nudgeDismissed && nudgeItems.length > 0;
  const visibleNudge = nudgeItems.slice(0, 6);

  return (
    <TooltipProvider delayDuration={200}>
    <div>
      <PageHeader title="Command" subtitle="Today at a glance — own your time and energy with grace." testid="life-command-header"
        actions={<>
          <div className="flex items-center gap-1 rounded-[10px] border border-[color:var(--border-subtle)] bg-white p-0.5">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="date-prev" aria-label="Previous day"><ChevronLeft className="h-4 w-4" /></Button>
            <button onClick={() => setDate(iso())} className={cn("px-2 text-sm min-w-[150px] transition-colors hover:text-[var(--green)]", isToday ? "text-[var(--text)]" : "text-[var(--gold)]")} data-testid="date-label">{isToday ? "Today · " : ""}{dayLabel(date)}</button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="date-next" aria-label="Next day"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <AddButton onClick={openNew} testid="add-action-button"><Plus className="h-4 w-4 mr-1.5" />Add action</AddButton>
        </>} />

      <SectionCard className="mb-4" testid="vision-card" title="Vision" subtitle={state.season}
        right={<Button variant="ghost" size="sm" onClick={openVision} data-testid="edit-vision-button" className="text-[var(--text-muted)]"><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>}>
        <p className="font-serif-display text-lg text-[var(--text)]">{state.identity}</p>
        <p className="text-sm text-[var(--text-muted)] mt-1">{state.calling}</p>
        <p className="text-sm text-[var(--text)] mt-3"><span className="text-[var(--gold)] font-medium">Focus:</span> {state.focus}</p>
      </SectionCard>

      <div className="mb-4 rounded-[var(--radius-card)] border border-[color:rgba(198,139,60,0.35)] bg-[color:rgba(198,139,60,0.08)] p-5" data-testid="ai-encouragement-card">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]"><Sparkles className="h-3.5 w-3.5" />A word for you today</div>
          <Button variant="ghost" size="sm" onClick={generateEnc} disabled={encLoading} data-testid="refresh-encouragement" className="text-[var(--text-muted)] h-7">
            <RefreshCw className={cn("h-3.5 w-3.5 mr-1", encLoading && "animate-spin")} />Refresh
          </Button>
        </div>
        {encLoading && !enc ? (
          <div className="mt-3 space-y-2"><Skeleton className="h-5 w-64" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-3/4" /></div>
        ) : enc ? (
          <div className="mt-2">
            <h3 className="font-serif-display text-xl text-[var(--text)]" data-testid="encouragement-title">{enc.title}</h3>
            <p className="text-sm text-[var(--text)] mt-1.5 leading-relaxed" data-testid="encouragement-message">{enc.message}</p>
            {enc.verse && <p className="text-[12px] text-[var(--gold)] mt-2 font-medium">{enc.verse}</p>}
          </div>
        ) : (
          <p className="text-sm text-[var(--text-muted)] mt-2">A personalized encouragement based on your day, energy and alignment will appear here.</p>
        )}
      </div>

      {showNudge && (
        <div className="mb-4 rounded-[var(--radius-card)] border border-[color:rgba(31,115,85,0.25)] bg-[color:rgba(31,115,85,0.06)] p-4" data-testid="smart-reminder-card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 text-[13px] text-[var(--text)]">
              <span className="grid place-items-center h-6 w-6 rounded-full bg-[color:rgba(31,115,85,0.14)] text-[var(--green)]"><Bell className="h-3.5 w-3.5" /></span>
              <span><span className="font-medium">A gentle nudge</span> — <span data-testid="nudge-count">{nudgeItems.length}</span> {nudgeItems.length === 1 ? "thing is" : "things are"} still open today. Tap to check one off.</span>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--text-muted)]" onClick={() => setNudgeDismissed(true)} data-testid="nudge-dismiss" aria-label="Dismiss reminder"><X className="h-3.5 w-3.5" /></Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {visibleNudge.map((it) => (
              <button
                key={`${it.kind}-${it.id}`}
                onClick={it.onDo}
                data-testid={`nudge-item-${it.kind}-${it.id}`}
                className="group inline-flex items-center gap-2 rounded-full border border-[color:var(--border-subtle)] bg-white pl-2.5 pr-3 py-1.5 text-[13px] text-[var(--text)] transition-colors hover:border-[color:var(--green)] hover:bg-[color:rgba(31,115,85,0.06)]"
              >
                <span className="grid place-items-center h-4 w-4 rounded-full border border-[color:var(--border-strong)] text-transparent group-hover:text-[var(--green)] group-hover:border-[var(--green)] transition-colors"><Check className="h-3 w-3" /></span>
                <span className="max-w-[220px] truncate">{it.label}</span>
                {it.kind === "habit" && <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: it.color }} />}
              </button>
            ))}
            {nudgeItems.length > visibleNudge.length && (
              <span className="inline-flex items-center text-[12px] text-[var(--text-muted)] px-1">+{nudgeItems.length - visibleNudge.length} more</span>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KpiCard label="Life score" value={m.lifeScore} sub="Whole-life balance" icon={Compass} accent testid="kpi-life-score" />
        <KpiCard label="Readiness" value={`${m.earned}/${m.planned}`} sub="Points earned today" icon={CheckCircle2} testid="kpi-readiness" />
        <KpiCard label="Energy" value={`${state.energy}%`} sub="Self-reported" icon={Flame} testid="kpi-energy" />
        <KpiCard label="Alignment" value={`${state.alignment}%`} sub="With faith & values" icon={Scale} testid="kpi-alignment" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 space-y-4">
          <SectionCard testid="brief-card">
            <div className="flex items-center gap-5">
              <Ring value={m.earned} />
              <div className="min-w-0">
                <StatusPill status={prioTone(m.brief.signal)} />
                <h3 className="font-serif-display text-xl text-[var(--text)] mt-2">{m.brief.title}</h3>
                <p className="text-sm text-[var(--text-muted)] mt-1">{m.brief.detail}</p>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Today's actions" subtitle={`${doneCount} of ${dayTasks.length} complete`} testid="today-actions-card"
            right={dayTasks.length > 0 && (
              <div className="w-28"><ProgressBar value={doneCount} max={dayTasks.length} tone={doneCount === dayTasks.length ? "green" : "gold"} /></div>
            )}>
            {/* Quick add */}
            <div className="flex items-center gap-2 mb-3">
              <Input
                value={quick}
                onChange={(e) => setQuick(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") quickAdd(); }}
                placeholder="Quick add an action, then press Enter…"
                data-testid="quick-add-input"
                className="bg-white border-[color:var(--border-subtle)]"
              />
              <Button onClick={quickAdd} disabled={!quick.trim()} data-testid="quick-add-button"
                className="shrink-0 bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]"><Plus className="h-4 w-4" /></Button>
            </div>

            {dayTasks.length === 0 ? (
              <EmptyState icon={CheckCircle2} title="No actions for this day" message="Add a one-off action, or set a repeat so it shows up automatically every day." action={<AddButton onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Add action</AddButton>} />
            ) : (
              <div className="space-y-2">
                {dayTasks.map((t) => {
                  const done = taskDoneOn(t, date);
                  return (
                    <div key={t.id} className={cn("group flex items-center gap-3 rounded-[10px] border px-3 py-2.5 transition-colors", done ? "border-[color:rgba(31,115,85,0.25)] bg-[color:rgba(31,115,85,0.05)]" : "border-[color:var(--border-subtle)] bg-white hover:border-[color:var(--border-strong)]")} data-testid={`task-row-${t.id}`}>
                      <button onClick={() => toggle(t)} aria-label="Toggle" data-testid={`task-toggle-${t.id}`} className="transition-transform active:scale-90">
                        {done ? <CheckCircle2 className="h-5 w-5 text-[var(--green)]" /> : <Circle className="h-5 w-5 text-[var(--text-faint)] group-hover:text-[var(--green)]" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className={cn("text-sm flex items-center gap-2", done ? "line-through text-[var(--text-faint)]" : "text-[var(--text)]")}>
                          <span className="truncate">{t.title}</span>
                          {isRecurring(t) && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[color:rgba(198,139,60,0.16)] px-1.5 py-0.5 text-[10px] font-medium text-[#8a5a17] shrink-0" data-testid={`task-repeat-${t.id}`}>
                              <Repeat className="h-2.5 w-2.5" />{recurrenceLabel[t.recurrence]}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="inline-flex items-center gap-1 text-[11px] text-[var(--text-faint)]">{timeLabel(t)}</span>
                          <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: areaColor(t.engine) }}><span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: areaColor(t.engine) }} />{t.engine}</span>
                        </div>
                      </div>
                      <span className="text-[11px] tabular-nums text-[var(--green)] font-medium shrink-0">+{t.points || pointsFor(t.priority)}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => openEdit(t)} data-testid={`task-edit-${t.id}`} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--danger)] opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => remove(t.id)} data-testid={`task-delete-${t.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>

          {/* Habit tracker */}
          <SectionCard title="Habit tracker" subtitle="Small, repeated choices that compound"
            testid="habit-tracker-card"
            right={<Button variant="ghost" size="sm" onClick={openNewHabit} data-testid="add-habit-button" className="text-[var(--green)]"><Plus className="h-3.5 w-3.5 mr-1" />Add habit</Button>}>
            {habits.length === 0 ? (
              <EmptyState icon={ListChecks} title="No habits yet" message="Track the daily disciplines you want to keep — a tap a day builds the streak." action={<AddButton onClick={openNewHabit}><Plus className="h-4 w-4 mr-1.5" />Add habit</AddButton>} />
            ) : (
              <div className="space-y-2">
                {/* Weekday header */}
                <div className="flex items-center gap-3 pl-1 pr-1">
                  <div className="flex-1" />
                  <div className="flex items-center gap-1.5">
                    {week.map((d) => (
                      <div key={d} className="w-7 text-center text-[10px] uppercase tracking-wide text-[var(--text-faint)]">{weekdayShort(d)}</div>
                    ))}
                  </div>
                  <div className="w-14 text-right text-[10px] uppercase tracking-wide text-[var(--text-faint)]">Streak</div>
                  <div className="w-7" />
                </div>
                {habits.map((h) => {
                  const streak = habitStreak(habitLog, h.id, date);
                  const total = habitTotal(habitLog, h.id);
                  const color = areaColor(h.area);
                  return (
                    <div key={h.id} className="group flex items-center gap-3 rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2.5" data-testid={`habit-row-${h.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-sm text-[var(--text)]">
                          <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                          <span className="truncate">{h.name}</span>
                        </div>
                        <div className="text-[11px] text-[var(--text-faint)] mt-0.5">{total} day{total === 1 ? "" : "s"} total</div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {week.map((d) => {
                          const on = habitDoneOn(habitLog, h.id, d);
                          const today = d === date;
                          return (
                            <Tooltip key={d}>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => toggleHabit(h.id, d)}
                                  data-testid={`habit-${h.id}-${d}`}
                                  aria-label={`${h.name} on ${d}`}
                                  className={cn(
                                    "grid place-items-center h-7 w-7 rounded-full border transition-all active:scale-90",
                                    on ? "text-white border-transparent" : "border-[color:var(--border-subtle)] text-transparent hover:border-[color:var(--border-strong)]",
                                    today && !on && "ring-2 ring-[color:rgba(198,139,60,0.4)] ring-offset-1",
                                    today && on && "ring-2 ring-offset-1 ring-[color:rgba(198,139,60,0.5)]"
                                  )}
                                  style={on ? { backgroundColor: color } : {}}
                                >
                                  <Check className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent><span className="text-xs">{dayLabel(d)}</span></TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                      <div className="w-14 flex items-center justify-end gap-1">
                        <Flame className={cn("h-4 w-4", streak > 0 ? "text-[var(--gold)]" : "text-[var(--text-faint)]")} />
                        <span className="text-sm tabular-nums text-[var(--text)]" data-testid={`habit-streak-${h.id}`}>{streak}</span>
                      </div>
                      <div className="flex items-center">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--text-muted)] hover:text-[var(--green)]" onClick={() => openInsights(h)} data-testid={`habit-insights-${h.id}`} aria-label="Habit insights"><BarChart3 className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => openEditHabit(h)} data-testid={`habit-edit-${h.id}`} aria-label="Edit habit"><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--danger)] opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => removeHabit(h.id)} data-testid={`habit-delete-${h.id}`} aria-label="Delete habit"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </div>

        <div className="lg:col-span-4 space-y-4">
          <div className="rounded-[var(--radius-card)] p-5 text-[#f7fbf9]" style={{ background: "linear-gradient(135deg,#1f7355,#17231f)" }} data-testid="verse-card">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#e7d9bf]"><BookOpen className="h-3.5 w-3.5" />Verse for today</div>
            <p className="font-serif-display text-lg leading-relaxed mt-3">“{verse.text}”</p>
            <p className="text-sm text-[#cfe3d8] mt-2">{verse.reference}</p>
          </div>

          <SectionCard title="Energy & alignment" testid="sliders-card">
            <div className="space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2"><span className="text-sm text-[var(--text-muted)]">Energy</span><span className="text-sm tabular-nums text-[var(--text)]">{state.energy}%</span></div>
                <Slider value={[state.energy]} min={0} max={100} step={1} onValueChange={(v) => setField("energy", v[0])} data-testid="energy-slider" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2"><span className="text-sm text-[var(--text-muted)]">Alignment</span><span className="text-sm tabular-nums text-[var(--text)]">{state.alignment}%</span></div>
                <Slider value={[state.alignment]} min={0} max={100} step={1} onValueChange={(v) => setField("alignment", v[0])} data-testid="alignment-slider" />
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Needs a gentle lift" testid="lowest-area-card">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-[10px] text-white shrink-0" style={{ backgroundColor: m.lowest.area.color }}><m.lowest.area.icon className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[var(--text)]">{m.lowest.area.name}</div>
                <div className="mt-1"><ProgressBar value={m.lowest.score} max={100} tone={m.lowest.score > 50 ? "green" : "gold"} /></div>
              </div>
              <span className="text-sm tabular-nums text-[var(--text)]">{m.lowest.score}</span>
            </div>
          </SectionCard>
        </div>
      </div>

      {draft && (
        <FormModal open={open} onOpenChange={setOpen} title={(state.tasks || []).some((x) => x.id === draft.id) ? "Edit action" : "Add action"} onSubmit={saveTask} testid="action-modal">
          <TextField label="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} full testid="action-title-input" placeholder="e.g. Morning devotion" />
          <SelectField label="Repeat" value={draft.recurrence} onChange={(v) => setDraft({ ...draft, recurrence: v })} options={recurrenceOpts} testid="action-recurrence-select" />
          <SelectField label="Life area" value={draft.engine} onChange={(v) => setDraft({ ...draft, engine: v })} options={areaOpts} testid="action-area-select" />
          <SelectField label="Priority" value={draft.priority} onChange={(v) => setDraft({ ...draft, priority: v, points: pointsFor(v) })} options={prioOpts} testid="action-priority-select" />
          <TextField label={draft.recurrence === "once" ? "Date" : "Starts on"} type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} testid="action-date-input" hint={draft.recurrence !== "once" ? recurrenceOpts.find((o) => o.value === draft.recurrence)?.label : undefined} />
          <TextField label="Start time" type="time" value={draft.time} onChange={(e) => setDraft({ ...draft, time: e.target.value })} testid="action-time-input" />
          <TextField label="End time" type="time" value={draft.endTime} onChange={(e) => setDraft({ ...draft, endTime: e.target.value })} testid="action-endtime-input" />
        </FormModal>
      )}

      {habitDraft && (
        <FormModal open={habitOpen} onOpenChange={setHabitOpen} title={(state.habits || []).some((x) => x.id === habitDraft.id) ? "Edit habit" : "Add habit"} onSubmit={saveHabit} testid="habit-modal">
          <TextField label="Habit" value={habitDraft.name} onChange={(e) => setHabitDraft({ ...habitDraft, name: e.target.value })} full testid="habit-name-input" placeholder="e.g. Drink 8 glasses of water" />
          <SelectField label="Life area" value={habitDraft.area} onChange={(v) => setHabitDraft({ ...habitDraft, area: v })} options={areaOpts} full testid="habit-area-select" />
        </FormModal>
      )}

      {insightHabit && (
        <HabitInsights key={insightHabit.id} habit={insightHabit} log={habitLog} color={areaColor(insightHabit.area)} open={insightOpen} onOpenChange={setInsightOpen} />
      )}

      {vision && (
        <FormModal open={visionOpen} onOpenChange={setVisionOpen} title="Edit vision" onSubmit={saveVision} testid="vision-modal">
          <TextField label="Identity" value={vision.identity} onChange={(e) => setVision({ ...vision, identity: e.target.value })} full testid="vision-identity-input" />
          <TextArea label="Calling" value={vision.calling} onChange={(e) => setVision({ ...vision, calling: e.target.value })} full testid="vision-calling-input" />
          <TextField label="Season" value={vision.season} onChange={(e) => setVision({ ...vision, season: e.target.value })} testid="vision-season-input" />
          <TextField label="Focus" value={vision.focus} onChange={(e) => setVision({ ...vision, focus: e.target.value })} testid="vision-focus-input" />
        </FormModal>
      )}
    </div>
    </TooltipProvider>
  );
}
